import { ClassifierType } from "models/ClassifierModel";
import { LLMConstants } from "models/LLMs/constants";
import {
    getLLMVariableNames,
    getPromptVariableReferences,
    isModelVariable,
    matchesLLMVariableName,
    rewritePromptVariableReferences,
    SlimLLMVariable,
} from "models/LLMs/LLMVariable";
import {
    Condition,
    ICondition,
} from "models/LLMs/LogicChaining/Conditions/Condition";
import { ConditionPrimitive } from "models/LLMs/LogicChaining/Conditions/ConditionPrimitive";
import {
    conditionExpressionByPhrase,
    conditionExpressions,
} from "models/LLMs/LogicChaining/Conditions/ConditionExpressionMeta";
import type { ModelChainResponse } from "models/LLMs/ModelChain";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import ClassifierService from "services/ClassifierService";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { v4 } from "uuid";
import { LLMClassifierBuilderStore } from "./LLMClassifierBuilderStore";
import {
    getLLMVariableValueControl,
    getLLMVariableValueOptions,
} from "../Components/LLMVariableValueField";
import {
    allConditionOperators,
    formatVersionLabel,
    getModelKind,
    modelKindLabel,
    operatorTakesValue,
    sharedModelName,
    summarizeOutputNames,
    wholeValueConditionOperators,
} from "./ChainSourceHelpers";
import type {
    ChainConditionOperator,
    ChainDeclaredInput,
    ChainDeclaredInputGroup,
    ChainDependentModel,
    ChainGraphLevel,
    ChainGraphModel,
    ChainModelKind,
    ChainPromptReference,
    ChainRunCondition,
    ChainSourceModel,
    ChainSourceVariable,
    ChainStepConditionReference,
    UnparentedPromptModelReference,
} from "./ChainSourceHelpers";

/**
 * State behind the Chain source section and the Model chain map on the builder
 * step.
 *
 * The models available as parents are the Core AI Models and LLM
 * Models the platform publishes as variables (`api/LLMClassifier/Variables`).
 * Saved parent/child edges, the version each relation records, and which outputs
 * a child consumes all come from the model-chain API.
 */
@AcxStore
export class ChainSourceStore extends BaseStore {
    public static Tasks = {
        LoadModelChain: "Load Model Chain",
    } as const;

    private readonly builderStore: LLMClassifierBuilderStore;
    private readonly classifierService: ClassifierService;
    private modelChainLoadPromise?: Promise<boolean>;
    @observable private failedModelChainId?: string;

    @observable parents: ChainSourceModel[] = [];
    @observable runConditions: ChainRunCondition[] = [];

    /**
     * The parents as last saved, kept separately because `chainGraph` is a draft:
     * adding or removing a parent edits its edges so the map can preview the
     * change, which leaves it unusable as a baseline to diff against.
     */
    @observable private savedParents: { id: string; name: string }[] = [];

    /** Set by links on other steps so the Dependencies section opens and scrolls into view on arrival. */
    public isRevealRequested = false;

    public requestReveal() {
        this.isRevealRequested = true;
    }

    public clearRevealRequest() {
        this.isRevealRequested = false;
    }

    /**
     * The outputs this model emits into the chain, taken from the variables the
     * platform publishes for it. A scalar model emits one; a structured output
     * emits one per schema field.
     */
    @computed get outputs(): ChainSourceVariable[] {
        const classifierId = this.builderStore.classifier.id;
        if (!classifierId) return [];

        return (
            this.availableModels.find((model) => model.id === classifierId)
                ?.variables ?? []
        );
    }

    @computed get output(): string {
        return this.outputs.length > 0
            ? summarizeOutputNames(
                this.outputs.map((variable) => variable.shortName),
            )
            : "no published output";
    }

    /** Models directly downstream of this one. */

    /**
     * The whole chain this model belongs to, so the map can show every ancestor
     * and descendant rather than only the models this one is wired to directly.
     */
    @observable private chainGraph?: ModelChainResponse;

    constructor(rootStore: IRootStore) {
        super("ChainSourceStore");
        makeObservable(this);

        this.builderStore = rootStore.getStore(LLMClassifierBuilderStore);
        this.classifierService = new ClassifierService();
        this.builderStore.registerModelComputeRelationsEditor(this);

        reaction(
            () => ({
                classifierId: this.builderStore.classifier.id,
                isSaved: this.builderStore.isSaved,
                versionNumber:
                    this.builderStore.classifier.implementation.versionNumber,
            }),
            (current, previous) => {
                const classifierChanged =
                    !previous || previous.classifierId !== current.classifierId;
                const versionChanged =
                    previous?.versionNumber !== current.versionNumber;

                // A different version declares a different parent set, so nothing carries over.
                if (classifierChanged || versionChanged) this.resetModelChain();

                if (
                    current.isSaved &&
                    (classifierChanged || !previous?.isSaved || versionChanged)
                ) {
                    void this.loadModelChain(current.classifierId);
                }
            },
            { fireImmediately: true },
        );
    }

    /**
     * Metadata is always available, but model outputs are valid inputs only when
     * their producing classifier is a direct parent of the model being edited.
     */
    public isVariableReferenceable(variable: SlimLLMVariable): boolean {
        if (!isModelVariable(variable.name)) return true;

        const sourceClassifierId = variable.modelOutputSource?.classifierId;
        return this.parents.some((parent) =>
            sourceClassifierId
                ? parent.id === sourceClassifierId
                : parent.variables.some(
                    (parentVariable) => parentVariable.name === variable.name,
                ),
        );
    }

    @action
    private resetModelChain() {
        this.parents = [];
        this.runConditions = [];
        this.savedParents = [];
        this.chainGraph = undefined;
        this.failedModelChainId = undefined;
    }

    private async loadModelChain(classifierId: string) {
        if (this.builderStore.classifier.id === classifierId) {
            this.failedModelChainId = undefined;
        }

        const loadTask = this.setupAsyncTask(
            ChainSourceStore.Tasks.LoadModelChain,
            async () => {
                // The chain names its parents and their outputs from the
                // published variables, so both have to be in hand before the
                // response can be turned into parents.
                const [modelChain] = await Promise.all([
                    this.classifierService.getModelChain(classifierId),
                    this.builderStore.variablesLoaded,
                ]);

                // Ignore a slow response after the editor has moved to another model.
                if (this.builderStore.classifier.id !== classifierId) return;

                this.applyModelChain(modelChain);
            },
        );
        const completion = loadTask.then(
            (result) => result !== AsyncTaskStatus.Error,
        );
        this.modelChainLoadPromise = completion;

        const succeeded = await completion;
        if (this.modelChainLoadPromise === completion) {
            this.modelChainLoadPromise = undefined;
        }
        if (!succeeded && this.builderStore.classifier.id === classifierId) {
            this.failedModelChainId = classifierId;
        }
    }

    @action
    private applyModelChain(modelChain: ModelChainResponse) {
        const nodeById = new Map(
            modelChain.nodes.map((node) => [node.classifierId, node]),
        );
        // The loaded version's declared parents, not the deployed edges, so an older version shows its own.
        const savedParents = this.builderStore.loadedVersionParents.map(
            (declaredParent) => {
                const availableModel = this.availableModels.find(
                    (model) => model.id === declaredParent.classifierId,
                );
                const node = nodeById.get(declaredParent.classifierId);

                return {
                    ...(availableModel ?? {
                        id: declaredParent.classifierId,
                        name:
                            declaredParent.name ??
                            node?.name ??
                            "Unavailable model",
                        kind: getModelKind(node),
                        variables: [],
                    }),
                    level: 1,
                };
            },
        );

        this.savedParents = savedParents.map((parent) => ({
            id: parent.id,
            name: parent.name,
        }));

        // A reload over an edited chain — a save records a new version, which
        // reloads it — keeps parents added in the editor since. They have no
        // saved edge to rebuild from, so rebuilding from the saved edges alone
        // would drop them along with the run condition and preview edge that
        // came with each.
        const savedParentIds = new Set(savedParents.map((parent) => parent.id));
        const draftParents = this.parents.filter(
            (parent) => !savedParentIds.has(parent.id),
        );
        const draftConditionsByParentId = new Map<
            string,
            ChainRunCondition[]
        >();
        for (const condition of this.runConditions) {
            if (savedParentIds.has(condition.parentId)) continue;
            const conditions =
                draftConditionsByParentId.get(condition.parentId) ?? [];
            conditions.push(condition);
            draftConditionsByParentId.set(condition.parentId, conditions);
        }

        this.parents = [...savedParents, ...draftParents];
        this.chainGraph = draftParents.reduce(
            (graph, parent) => this.addParentToDraftGraph(parent, graph),
            modelChain,
        );

        this.runConditions = this.parents.flatMap((parent) => {
            const draftConditions = draftConditionsByParentId.get(parent.id);
            if (draftConditions) return draftConditions;

            return this.buildRunConditions(parent);
        });
    }

    /** Existing conditions move with a parent; otherwise presence is the default gate. */
    private buildRunConditions(parent: ChainSourceModel): ChainRunCondition[] {
        const firstVariable = parent.variables[0];
        if (!firstVariable) return [];

        const matchingConditions =
            this.builderStore.classifier.implementation.configurationBuilder.executeConditions.filter(
                (condition): condition is ConditionPrimitive =>
                    condition instanceof ConditionPrimitive &&
                    (condition.classifierId === parent.id ||
                        parent.variables.some((variable) =>
                            matchesLLMVariableName(
                                variable.source,
                                condition.propertyName,
                            ),
                        )),
            );

        if (matchingConditions.length === 0) {
            return [
                {
                    id: v4(),
                    parentId: parent.id,
                    variable: firstVariable.name,
                    operator: "is defined",
                    value: undefined,
                },
            ];
        }

        return matchingConditions.map((savedCondition) => {
            const variable =
                parent.variables.find((candidate) =>
                    matchesLLMVariableName(
                        candidate.source,
                        savedCondition.propertyName,
                    ),
                ) ?? firstVariable;

            return {
                id: savedCondition.id,
                parentId: parent.id,
                variable: variable.name,
                operator: variable.source.presenceOnly
                    ? "is defined"
                    : conditionExpressions[savedCondition.operation].phrase,
                value: savedCondition.value,
            };
        });
    }

    /** Resolves once an in-flight chain load has landed, since it rebuilds parents and run conditions. */
    public async whenChainSettled(): Promise<void> {
        await this.modelChainLoadPromise;
    }

    /**
     * The parent list as saved and as edited, so the save dialog can report a
     * parent added or removed. Parents are saved alongside the classifier but
     * are not part of its response, so the diff cannot read them off the
     * before/after snapshots the way it reads everything else.
     */
    public getParentModelNamesForDiff(): { before: string[]; after: string[] } {
        return {
            before: this.savedParents.map((parent) => parent.name).sort(),
            after: this.parents.map((parent) => parent.name).sort(),
        };
    }

    /**
     * Names of the LLM models this one is chained to, in the order the chain
     * lists them. A Core AI parent is not included: only a dependency on
     * another LLM model stops the model from being published.
     */
    public getLLMParentModelNames(): string[] {
        return this.parents
            .filter((parent) => parent.kind === "llm")
            .map((parent) => parent.name);
    }

    public async getParentModelsForSave(childClassifierId: string) {
        const activeLoad = this.modelChainLoadPromise;
        const loadFailed = activeLoad ? !(await activeLoad) : false;
        if (loadFailed || this.failedModelChainId === childClassifierId) {
            throw new Error(
                "Parent models could not be loaded. Retry loading the model chain before saving.",
            );
        }
        const unresolvedReferences =
            this.getUnparentedConfigurationReferences();
        if (unresolvedReferences.length > 0) {
            throw new Error(
                `Add the referenced parent model${unresolvedReferences.length === 1 ? "" : "s"
                } before saving: ${unresolvedReferences
                    .map((reference) => reference.model.name)
                    .join(", ")}.`,
            );
        }

        return this.parents.map((parent) => ({
            classifierId: parent.id,
        }));
    }

    /**
     * Finds complete, recognized model tokens in a prompt whose producing model
     * has not been declared as a parent. Unknown or partially typed tokens are
     * left alone so a typo does not silently create a dependency.
     */
    public getUnparentedPromptReferences(
        prompt: string,
    ): UnparentedPromptModelReference[] {
        const referencedVariableNames = new Set(
            getPromptVariableReferences(prompt),
        );

        return this.availableModels.flatMap((model) => {
            if (
                model.id === this.builderStore.classifier.id ||
                this.parents.some((parent) => parent.id === model.id)
            ) {
                return [];
            }

            const variables = model.variables
                .flatMap((variable) => getLLMVariableNames(variable.source))
                .filter((name) => referencedVariableNames.has(name));

            return variables.length > 0 ? [{ model, variables }] : [];
        });
    }

    /** Every unresolved model token in every prompt in the current workflow. */
    public getUnparentedConfigurationReferences(): UnparentedPromptModelReference[] {
        const implementation = this.builderStore.classifier.implementation;
        if (!implementation.isLogicChain()) return [];

        const referencesByModel = new Map<
            string,
            UnparentedPromptModelReference
        >();

        for (const step of implementation.logicChainBuilder.steps) {
            for (const reference of this.getUnparentedPromptReferences(
                step.configuration.prompt,
            )) {
                const existing = referencesByModel.get(reference.model.id);
                if (existing) {
                    existing.variables = [
                        ...new Set([
                            ...existing.variables,
                            ...reference.variables,
                        ]),
                    ];
                } else {
                    referencesByModel.set(reference.model.id, {
                        model: reference.model,
                        variables: [...reference.variables],
                    });
                }
            }
        }

        return [...referencesByModel.values()];
    }

    /**
     * Maps every variable one classifier backs onto a single parent option.
     * Variable values are the results the model can write.
     */
    private toChainSourceModel(
        id: string,
        variables: SlimLLMVariable[],
        kind: ChainModelKind,
        prefix: string,
    ): ChainSourceModel {
        // Model names can contain dots, so take everything past the prefix
        // rather than splitting on ".".
        const shortNames = variables.map((variable) =>
            variable.name.slice(prefix.length + 1),
        );
        const modelName =
            variables.find((variable) => variable.modelOutputSource)
                ?.modelOutputSource?.classifierName ??
            sharedModelName(shortNames);
        const outputNames = variables.map((variable, index) => {
            const name = shortNames[index];
            return name.startsWith(`${modelName}.`)
                ? name.slice(modelName.length + 1)
                : name;
        });

        return {
            id,
            name: modelName,
            kind,
            // Nothing models grandparents yet, so every parent is one level up.
            level: 1,
            variables: variables.map((variable, index) => ({
                name: variable.name,
                label: variable.label,
                shortName: outputNames[index],
                source: variable,
                values: getLLMVariableValueOptions(variable),
            })),
            hasComputeRestrictions:
                kind === "ml"
                    ? this.builderStore.mlModelInformation.find(
                        (model) => model.id === id,
                    )?.hasComputeRestrictions
                    : undefined,
        };
    }

    /**
     * Every model that can be used as a parent, in the same two groups the
     * metadata filter dropdown on the Volume Control step shows. Variables are
     * grouped by the classifier behind them, so a model that emits more than one
     * output is one parent with a choice of output.
     */
    @computed get availableModelGroups(): {
        label: string;
        kind: ChainModelKind;
        options: ChainSourceModel[];
    }[] {
        const variables = this.builderStore.variableGroups.flatMap(
            (group) => group.variables,
        );

        return [
            {
                label: "Core AI Models",
                kind: "ml" as ChainModelKind,
                prefix: LLMConstants.MLModelsFullyQualifiedName,
            },
            {
                label: "LLM Models",
                kind: "llm" as ChainModelKind,
                prefix: LLMConstants.LLMModelsFullyQualifiedName,
            },
        ].map(({ prefix, ...group }) => {
            const byClassifier = new Map<string, SlimLLMVariable[]>();

            for (const variable of variables) {
                if (!variable.name.startsWith(`${prefix}.`)) continue;

                // Variables the platform has not attributed to a classifier fall
                // back to standing on their own, keyed by variable name.
                const key =
                    variable.modelOutputSource?.classifierId ?? variable.name;
                const existing = byClassifier.get(key);

                if (existing) existing.push(variable);
                else byClassifier.set(key, [variable]);
            }

            return {
                ...group,
                options: Array.from(byClassifier, ([id, classifierVariables]) =>
                    this.toChainSourceModel(
                        id,
                        classifierVariables,
                        group.kind,
                        prefix,
                    ),
                ),
            };
        });
    }

    /** Every model in the tenant that could be used as a parent. */
    @computed get availableModels(): ChainSourceModel[] {
        return this.availableModelGroups.flatMap((group) => group.options);
    }

    /** Models that can still be added as a parent, grouped by where they came from. */
    @computed get addableModelGroups(): {
        label: string;
        options: ChainSourceModel[];
    }[] {
        return this.availableModelGroups
            .map((group) => ({
                label: group.label,
                options: group.options.filter(
                    (model) =>
                        model.id !== this.builderStore.classifier.id &&
                        !this.parents.some((parent) => parent.id === model.id),
                ),
            }))
            .filter((group) => group.options.length > 0);
    }

    /**
     * True until the parents and their run conditions are in hand. Screens that
     * only read the chain wait on this: before it clears there are no parents, so
     * every ownership test answers "not chain owned" rather than "not yet known".
     */
    @computed get isLoadingChain(): boolean {
        return (
            this.builderStore.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.LoadVariables,
            ) || this.getTaskLoading(ChainSourceStore.Tasks.LoadModelChain)
        );
    }

    /** True when the open model's chain failed to load, so chain ownership of its conditions is unknown. */
    @computed get hasChainLoadFailed(): boolean {
        return this.failedModelChainId === this.builderStore.classifier.id;
    }

    @computed get isLoadingAvailableModels(): boolean {
        return this.isLoadingChain;
    }

    /** Inputs the prompt can reference, one per parent output plus the transcript. */
    @computed get declaredInputs(): ChainDeclaredInput[] {
        return [
            { name: "transcript", origin: "source" },
            ...this.parents.flatMap((parent) =>
                parent.variables.map((variable) => ({
                    name: variable.shortName,
                    // A model's variable is often named after the model itself,
                    // so "from <model>" would just repeat the input name. Naming
                    // the group instead also tells two same-named models apart.
                    origin:
                        variable.shortName === parent.name
                            ? modelKindLabel(parent.kind)
                            : `from ${parent.name}`,
                    parentId: parent.id,
                    variableName: variable.name,
                })),
            ),
        ];
    }

    /**
     * Walks the chain away from this model one level at a time. Following child
     * to parent edges gives ancestors, parent to child gives descendants. A node
     * is only reported at the shortest distance it is reachable at, which also
     * stops a cycle from looping forever.
     */
    private traverseChain(direction: "up" | "down"): ChainGraphLevel[] {
        const graph = this.chainGraph;
        if (!graph) return [];

        const nodeById = new Map(
            graph.nodes.map((node) => [node.classifierId, node]),
        );
        const levels: ChainGraphLevel[] = [];
        const visited = new Set<string>([graph.rootClassifierId]);

        let frontier = [graph.rootClassifierId];
        let level = 1;

        while (frontier.length > 0) {
            const nextFrontier: string[] = [];
            const models: ChainGraphModel[] = [];

            for (const classifierId of frontier) {
                for (const edge of graph.edges) {
                    const isConnected =
                        direction === "up"
                            ? edge.childClassifierId === classifierId
                            : edge.parentClassifierId === classifierId;
                    if (!isConnected) continue;

                    const nextId =
                        direction === "up"
                            ? edge.parentClassifierId
                            : edge.childClassifierId;
                    if (visited.has(nextId)) continue;

                    visited.add(nextId);
                    nextFrontier.push(nextId);

                    const node = nodeById.get(nextId);
                    models.push({
                        id: nextId,
                        name: node?.name ?? "Unavailable model",
                        kind: getModelKind(node),
                        level,
                        isPublished: node?.isPublished ?? false,
                        versionLabel: formatVersionLabel(
                            direction === "up"
                                ? edge.parentImplementationCreatedOn
                                : edge.childImplementationCreatedOn,
                        ),
                    });
                }
            }

            if (models.length > 0) levels.push({ level, models });

            frontier = nextFrontier;
            level += 1;
        }

        return levels;
    }

    /**
     * Add an unsaved direct-parent edge to the graph used by the chain preview.
     * The persisted relation remains untouched until the model itself is saved.
     */
    private addParentToDraftGraph(
        model: ChainSourceModel,
        graph = this.chainGraph,
    ): ModelChainResponse {
        const classifier = this.builderStore.classifier;
        const rootClassifierId = classifier.id;
        const nodes = new Map(
            (graph?.nodes ?? []).map((node) => [node.classifierId, node]),
        );

        if (!nodes.has(rootClassifierId)) {
            nodes.set(rootClassifierId, {
                classifierId: rootClassifierId,
                name: classifier.name ?? "Untitled model",
                classifierTypeName: ClassifierType.LLM,
                isPublished: classifier.isPublished,
                isActive: classifier.isActive,
            });
        }
        if (!nodes.has(model.id)) {
            nodes.set(model.id, {
                classifierId: model.id,
                name: model.name,
                classifierTypeName:
                    model.kind === "llm"
                        ? ClassifierType.LLM
                        : ClassifierType.Tensorflow,
                isPublished: false,
            });
        }

        const edges = [...(graph?.edges ?? [])];
        const hasDirectEdge = edges.some(
            (edge) =>
                edge.parentClassifierId === model.id &&
                edge.childClassifierId === rootClassifierId,
        );
        if (!hasDirectEdge) {
            edges.push({
                relationId: `draft-parent:${model.id}:${rootClassifierId}`,
                parentClassifierId: model.id,
                parentClassifierImplementationId:
                    model.implementationId ?? null,
                childClassifierId: rootClassifierId,
                childClassifierImplementationId: classifier.implementation.id,
            });
        }

        return {
            rootClassifierId,
            nodes: Array.from(nodes.values()),
            edges,
        };
    }

    /**
     * Reuse the existing chain endpoint to make a newly selected parent's own
     * ancestors visible immediately, without waiting for this model to be saved.
     */
    private async mergeParentChainIntoDraft(parentId: string) {
        const rootClassifierId = this.builderStore.classifier.id;

        try {
            const parentGraph = await this.classifierService.getModelChain(
                parentId,
            );

            // The user may have removed the parent or opened another model while
            // this request was in flight.
            if (
                this.builderStore.classifier.id !== rootClassifierId ||
                !this.parents.some((parent) => parent.id === parentId)
            )
                return;

            runInAction(() => {
                const currentGraph = this.chainGraph;
                const nodes = new Map(
                    (currentGraph?.nodes ?? []).map((node) => [
                        node.classifierId,
                        node,
                    ]),
                );
                for (const node of parentGraph.nodes) {
                    nodes.set(node.classifierId, node);
                }

                const edgeKey = (parentId: string, childId: string) =>
                    `${parentId}:${childId}`;
                const edges = new Map(
                    (currentGraph?.edges ?? []).map((edge) => [
                        edgeKey(
                            edge.parentClassifierId,
                            edge.childClassifierId,
                        ),
                        edge,
                    ]),
                );
                for (const edge of parentGraph.edges) {
                    edges.set(
                        edgeKey(
                            edge.parentClassifierId,
                            edge.childClassifierId,
                        ),
                        edge,
                    );
                }

                const parent = this.getParent(parentId);
                if (!parent) return;

                this.chainGraph = this.addParentToDraftGraph(parent, {
                    rootClassifierId,
                    nodes: Array.from(nodes.values()),
                    edges: Array.from(edges.values()),
                });
            });
        } catch {
            // The direct parent is already in the preview, and saving reports
            // any relation failure through the normal save path.
        }
    }

    /** Disconnect an unsaved parent; traversal naturally hides its detached branch. */
    private removeParentFromDraftGraph(parentId: string) {
        const graph = this.chainGraph;
        if (!graph) return;

        const rootClassifierId = this.builderStore.classifier.id;
        this.chainGraph = {
            ...graph,
            rootClassifierId,
            edges: graph.edges.filter(
                (edge) =>
                    !(
                        edge.parentClassifierId === parentId &&
                        edge.childClassifierId === rootClassifierId
                    ),
            ),
        };
    }

    /**
     * Models directly downstream of this one, with the outputs of ours each reads.
     *
     * Derived rather than captured when the chain loads: the labels read from
     * this model's published variables, which arrive on their own payload, and
     * the two responses land in either order.
     */
    @computed get dependents(): ChainDependentModel[] {
        const graph = this.chainGraph;
        if (!graph) return [];

        const nodeById = new Map(
            graph.nodes.map((node) => [node.classifierId, node]),
        );

        return graph.edges
            .filter(
                (edge) => edge.parentClassifierId === graph.rootClassifierId,
            )
            .map((edge) => {
                const node = nodeById.get(edge.childClassifierId);

                return {
                    id: edge.childClassifierId,
                    name: node?.name ?? "Unavailable model",
                    kind: getModelKind(node),
                    consumes: this.consumedOutputNames(
                        node?.referencedModelVariables ?? [],
                    ),
                };
            });
    }

    /**
     * How this model names itself wherever the chain refers to its outputs. The
     * published variables are the authority, since a rename only reaches the
     * variables the platform publishes once the model is saved.
     */
    @computed private get modelName(): string {
        const classifierId = this.builderStore.classifier.id;
        const published = classifierId
            ? this.availableModels.find((model) => model.id === classifierId)
            : undefined;

        return published?.name ?? this.builderStore.classifier.name ?? "";
    }

    /**
     * Which of this model's outputs a dependent reads, named the way the
     * dependent references them rather than by intersecting with what this model
     * publishes now. A child still reading an output this model has stopped
     * emitting is exactly what the chain exists to surface.
     */
    private consumedOutputNames(references: readonly string[]): string[] {
        const modelName = this.modelName;
        if (!modelName) return [];

        const shortNameByName = new Map(
            this.outputs.flatMap((output) =>
                getLLMVariableNames(output.source).map(
                    (name) => [name, output.shortName] as const,
                ),
            ),
        );
        // A scalar model is referenced as the model itself, a structured one as
        // a field beneath it, so both forms have to be recognized.
        const modelPrefixes = [
            `${LLMConstants.LLMModelsFullyQualifiedName}.${this.builderStore.classifier.id}`,
            `${LLMConstants.LLMModelsFullyQualifiedName}.${modelName}`,
        ];
        const consumed = new Set<string>();

        for (const reference of references) {
            const publishedShortName = shortNameByName.get(reference);

            if (publishedShortName) {
                consumed.add(publishedShortName);
            } else {
                const modelPrefix = modelPrefixes.find(
                    (prefix) =>
                        reference === prefix || reference.startsWith(`${prefix}.`),
                );
                if (reference === modelPrefix) {
                    consumed.add(modelName);
                } else if (modelPrefix) {
                    consumed.add(reference.slice(modelPrefix.length + 1));
                }
            }
        }

        return [...consumed];
    }

    /** Every model upstream of this one, nearest level first. */
    @computed get ancestorLevels(): ChainGraphLevel[] {
        return this.traverseChain("up");
    }

    /** Every model downstream of this one, nearest level first. */
    @computed get descendantLevels(): ChainGraphLevel[] {
        return this.traverseChain("down");
    }

    /**
     * The same inputs as `declaredInputs`, collapsed to one entry per source so a
     * parent that emits several outputs reads as one line.
     */
    @computed get declaredInputGroups(): ChainDeclaredInputGroup[] {
        return [
            { key: "transcript", name: "transcript", detail: "source" },
            ...this.parents.map((parent) => {
                const outputs = parent.variables.map(
                    (variable) => variable.shortName,
                );
                // A model whose only output is named after itself would just
                // repeat itself, so name the group it came from instead.
                const isSelfNamed =
                    outputs.length === 1 && outputs[0] === parent.name;

                return {
                    key: parent.id,
                    parentId: parent.id,
                    name: parent.name,
                    detail: isSelfNamed
                        ? modelKindLabel(parent.kind)
                        : summarizeOutputNames(outputs),
                    fullDetail: outputs.join(", "),
                };
            }),
        ];
    }

    /** Parents that only run on a subset of conversations. */
    @computed get restrictedParents(): ChainSourceModel[] {
        return this.parents.filter((parent) => parent.hasComputeRestrictions);
    }

    /** How far above this model a parent sits. */
    public getLevel(parentId: string): number {
        return this.getParent(parentId)?.level ?? 1;
    }

    /**
     * Parents grouped by level, nearest level first. Parents on the same level
     * are peers: this model runs only when every one of their gates passes.
     */
    @computed get parentLevels(): {
        level: number;
        parents: ChainSourceModel[];
    }[] {
        const byLevel = new Map<number, ChainSourceModel[]>();

        for (const parent of this.parents) {
            const existing = byLevel.get(parent.level);

            if (existing) existing.push(parent);
            else byLevel.set(parent.level, [parent]);
        }

        return Array.from(byLevel, ([level, parents]) => ({
            level,
            parents,
        })).sort((a, b) => a.level - b.level);
    }

    public getRunConditions(parentId: string): ChainRunCondition[] {
        return this.runConditions.filter(
            (condition) => condition.parentId === parentId,
        );
    }

    public getRunCondition(conditionId: string): ChainRunCondition | undefined {
        return this.runConditions.find(
            (condition) => condition.id === conditionId,
        );
    }

    public getParent(parentId: string): ChainSourceModel | undefined {
        return this.parents.find((parent) => parent.id === parentId);
    }

    /** The parent variable a run condition compares against. */
    public getConditionVariable(
        conditionId: string,
    ): ChainSourceVariable | undefined {
        const condition = this.getRunCondition(conditionId);
        if (!condition) return undefined;

        const parent = this.getParent(condition.parentId);

        return parent?.variables.find(
            (variable) => variable.name === condition.variable,
        );
    }

    /** Operators the gate on a parent can use, decided by the output it reads. */
    public getConditionOperators(conditionId: string): ChainConditionOperator[] {
        const variable = this.getConditionVariable(conditionId)?.source;

        // A structured output is only tested for presence.
        if (variable?.presenceOnly) return ["is defined"];

        // Substring comparisons need free text to search, so an output that is
        // picked from a set is compared whole. Same rule as the metadata filters
        // on the Volume Control step.
        return getLLMVariableValueControl(variable).type === "freeText"
            ? allConditionOperators
            : wholeValueConditionOperators;
    }

    /** The gates a parent puts on this model, joined the same way execution joins them. */
    public describeGate(parentId: string): string | undefined {
        const descriptions = this.getRunConditions(parentId)
            .map((condition) => this.describeRunCondition(condition))
            .filter((description): description is string => !!description);

        return descriptions.length > 0
            ? descriptions.join(" AND ")
            : undefined;
    }

    private describeRunCondition(
        condition: ChainRunCondition,
    ): string | undefined {
        const variable = this.getConditionVariable(condition.id);
        if (!condition || !variable) return undefined;

        const symbol = conditionExpressionByPhrase[condition.operator].symbol;
        if (!operatorTakesValue(condition.operator))
            return `${variable.shortName} ${symbol}`;

        // Show the value's label, since some variables store something other
        // than what they display.
        const value = this.isRunConditionComplete(condition.id)
            ? variable.values.find((option) => option.value === condition.value)
                ?.label ?? condition.value
            : "…";

        return `${variable.shortName} ${symbol} ${value}`;
    }

    /**
     * A gate that compares against a value needs one chosen. Presence checks
     * (`is defined`) are complete on their own.
     */
    public isRunConditionComplete(conditionId: string): boolean {
        const condition = this.getRunCondition(conditionId);
        if (!condition) return false;
        if (!operatorTakesValue(condition.operator)) return true;

        return !!condition.value && condition.value.trim() !== "";
    }

    /**
     * A parent publishes nothing to gate on when the platform lists no variables
     * for it -- an unpublished or removed model, whose outputs are gone while the
     * saved relation remains. There is no gate to complete for one of those, so it
     * is not waiting on anything either.
     */
    private canGateOnParent(parentId: string): boolean {
        return (this.getParent(parentId)?.variables.length ?? 0) > 0;
    }

    /**
     * Parents still waiting on a run condition value. A parent with no outputs is
     * excluded: it carries no condition, which would otherwise read as incomplete
     * forever with no field to fill in.
     */
    @computed get incompleteParents(): ChainSourceModel[] {
        return this.parents.filter(
            (parent) =>
                this.canGateOnParent(parent.id) &&
                this.getRunConditions(parent.id).some(
                    (condition) =>
                        !this.isRunConditionComplete(condition.id),
                ),
        );
    }

    @computed get hasIncompleteRunConditions(): boolean {
        return this.incompleteParents.length > 0;
    }

    @action
    public addParent(model: ChainSourceModel) {
        if (this.parents.some((parent) => parent.id === model.id)) return;

        this.parents = [...this.parents, model];
        const runConditions = this.buildRunConditions(model);
        this.runConditions = [...this.runConditions, ...runConditions];

        // Reusing a legacy condition also records its parent classifier ID so
        // the relationship remains stable if the model is later renamed.
        for (const runCondition of runConditions) {
            this.syncRunCondition(runCondition);
        }
        this.chainGraph = this.addParentToDraftGraph(model);
        void this.mergeParentChainIntoDraft(model.id);
    }

    @action
    public addRunCondition(parentId: string) {
        const variable = this.getParent(parentId)?.variables[0];
        if (!variable) return;

        const condition: ChainRunCondition = {
            id: v4(),
            parentId,
            variable: variable.name,
            operator: "is defined",
            value: undefined,
        };
        this.runConditions = [...this.runConditions, condition];
        this.syncRunCondition(condition);
    }

    @action
    public removeRunCondition(conditionId: string) {
        const condition = this.getRunCondition(conditionId);
        if (!condition || this.getRunConditions(condition.parentId).length <= 1)
            return;

        const configurationBuilder =
            this.builderStore.classifier.implementation.configurationBuilder;
        const executeCondition = configurationBuilder.executeConditions.find(
            (candidate) => candidate.id === conditionId,
        );
        if (executeCondition) {
            configurationBuilder.removeExecuteCondition(executeCondition);
        }
        this.runConditions = this.runConditions.filter(
            (candidate) => candidate.id !== conditionId,
        );
    }

    @action
    public removeParent(parentId: string) {
        const parent = this.getParent(parentId);
        const configurationBuilder =
            this.builderStore.classifier.implementation.configurationBuilder;

        if (parent) {
            const parentVariableNames = new Set(
                parent.variables.flatMap((variable) =>
                    getLLMVariableNames(variable.source),
                ),
            );
            for (const condition of [
                ...configurationBuilder.executeConditions,
            ]) {
                if (
                    condition instanceof ConditionPrimitive &&
                    (condition.classifierId === parent.id ||
                        parentVariableNames.has(condition.propertyName))
                ) {
                    configurationBuilder.removeExecuteCondition(condition);
                }
            }

            // A prompt referencing an output this model no longer receives would
            // leave an unresolvable placeholder, which the executor treats as a
            // fatal error, so the references go with the parent.
            this.removePromptReferences(parent);
            this.removeStepConditionReferences(parent);
        }

        this.parents = this.parents.filter(
            (candidate) => candidate.id !== parentId,
        );
        this.runConditions = this.runConditions.filter(
            (condition) => condition.parentId !== parentId,
        );
        this.removeParentFromDraftGraph(parentId);
    }

    /**
     * Where a parent's outputs are referenced in this model's prompts. Empty for
     * a standard model, which has no prompts of its own to reference them from.
     */
    public getPromptReferences(parentId: string): ChainPromptReference[] {
        const parent = this.getParent(parentId);
        const implementation = this.builderStore.classifier.implementation;
        if (!parent || !implementation.isLogicChain()) return [];

        return implementation.logicChainBuilder.steps.flatMap((step) => {
            const promptVariableNames = new Set(
                getPromptVariableReferences(step.configuration.prompt),
            );
            const referenced = parent.variables.filter((variable) =>
                getLLMVariableNames(variable.source).some((name) =>
                    promptVariableNames.has(name),
                ),
            );
            if (referenced.length === 0) return [];

            return [
                {
                    stepId: step.id,
                    stepName: step.name,
                    variables: referenced.map((variable) => variable.shortName),
                },
            ];
        });
    }

    @action
    private removePromptReferences(parent: ChainSourceModel) {
        const implementation = this.builderStore.classifier.implementation;
        if (!implementation.isLogicChain()) return;

        for (const step of implementation.logicChainBuilder.steps) {
            const original = step.configuration.prompt;
            const parentVariableNames = new Set(
                parent.variables.flatMap((variable) =>
                    getLLMVariableNames(variable.source),
                ),
            );
            const prompt = rewritePromptVariableReferences(
                original,
                (name) => (parentVariableNames.has(name) ? "" : undefined),
            );

            if (prompt !== original) step.configuration.setPrompt(prompt);
        }
    }

    /**
     * Conditions inside a step's IF/THEN actions that test a parent's outputs.
     * These are separate from the model level execute conditions and would
     * otherwise keep gating on a model that is no longer a dependency.
     */
    public getStepConditionReferences(
        parentId: string,
    ): ChainStepConditionReference[] {
        const parent = this.getParent(parentId);
        const implementation = this.builderStore.classifier.implementation;
        if (!parent || !implementation.isLogicChain()) return [];

        const variableByName = new Map(
            parent.variables.flatMap((variable) =>
                getLLMVariableNames(variable.source).map(
                    (name) => [name, variable] as const,
                ),
            ),
        );

        return implementation.logicChainBuilder.steps.flatMap((step) => {
            const referenced = new Set<string>();
            let losesWholeAction = false;

            for (const action of step.configuration.actions) {
                const propertyNames = action.conditions.flatMap((condition) =>
                    Condition.ExtractPropertyNames(condition),
                );
                const matched = propertyNames.filter((propertyName) =>
                    variableByName.has(propertyName),
                );
                if (matched.length === 0) continue;

                for (const propertyName of matched) {
                    referenced.add(variableByName.get(propertyName)!.shortName);
                }

                // Every condition on the action tests this parent, so the action
                // has nothing left to fire on once they are gone.
                if (matched.length === propertyNames.length) {
                    losesWholeAction = true;
                }
            }

            if (referenced.size === 0) return [];

            return [
                {
                    stepId: step.id,
                    stepName: step.name,
                    variables: [...referenced],
                    losesWholeAction,
                },
            ];
        });
    }

    @action
    private removeStepConditionReferences(parent: ChainSourceModel) {
        const implementation = this.builderStore.classifier.implementation;
        if (!implementation.isLogicChain()) return;

        const parentVariableNames = new Set(
            parent.variables.flatMap((variable) =>
                getLLMVariableNames(variable.source),
            ),
        );

        const referencesParent = (condition: ICondition): boolean =>
            Condition.ExtractPropertyNames(condition).some((propertyName) =>
                parentVariableNames.has(propertyName),
            );

        for (const step of implementation.logicChainBuilder.steps) {
            for (const action of [...step.configuration.actions]) {
                for (const condition of [...action.conditions]) {
                    if (referencesParent(condition))
                        action.removeCondition(condition);
                }

                // An action with no conditions left would fire unconditionally,
                // which is a quieter change in behaviour than removing it.
                if (action.conditions.length === 0)
                    step.configuration.removeAction(action);
            }
        }
    }

    @action
    public setConditionVariable(conditionId: string, variableName: string) {
        const condition = this.getRunCondition(conditionId);
        if (!condition) return;

        const variable = this.getParent(condition.parentId)?.variables.find(
            (candidate) => candidate.name === variableName,
        );
        if (!variable) return;

        // Values belong to the variable, so the old one cannot carry over. Only
        // pick a default for a gate that already had a value chosen; one that is
        // still waiting on a choice stays that way.
        const hadValue = this.isRunConditionComplete(conditionId);

        condition.variable = variable.name;
        condition.operator = variable.source.presenceOnly
            ? "is defined"
            : "equals";
        condition.value = variable.source.presenceOnly
            ? undefined
            : hadValue
                ? variable.values[0]?.value
                : undefined;
        this.syncRunCondition(condition);
    }

    @action
    public setConditionOperator(
        conditionId: string,
        operator: ChainConditionOperator,
    ) {
        const condition = this.getRunCondition(conditionId);
        if (!condition) return;

        // The value is left alone: presence operators ignore it, so switching to
        // one and back restores the previous choice rather than losing it.
        condition.operator = operator;
        this.syncRunCondition(condition);
    }

    @action
    public setConditionValue(conditionId: string, value: string) {
        const condition = this.getRunCondition(conditionId);
        if (!condition) return;

        condition.value = value;
        this.syncRunCondition(condition);
    }

    private syncRunCondition(condition: ChainRunCondition) {
        const configurationBuilder =
            this.builderStore.classifier.implementation.configurationBuilder;
        const parent = this.getParent(condition.parentId);
        let executeCondition = configurationBuilder.executeConditions.find(
            (candidate) => candidate.id === condition.id,
        ) as ConditionPrimitive | undefined;

        if (!executeCondition) {
            executeCondition = new ConditionPrimitive({
                id: condition.id,
                propertyName: "",
                operation: conditionExpressionByPhrase[condition.operator]
                    .type,
                value: condition.value ?? "",
            });
            configurationBuilder.addExecuteCondition(executeCondition);
        }

        const variable = parent?.variables.find(
            (candidate) => candidate.name === condition.variable,
        );
        executeCondition.setPropertyName(condition.variable);
        if (variable) executeCondition.setShortPropertyName(variable.label);
        executeCondition.setClassifierId(parent?.id);
        executeCondition.setOperation(
            conditionExpressionByPhrase[condition.operator].type,
        );
        executeCondition.setValue(condition.value ?? "");
    }

}
